import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Insights.css';
import { ROUTES, getInsightDetailPath } from '../../utils/routes';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const insightsBanner = PUBLIC_URL + '/images/blog/blog-1.jpg';

const sampleImage = PUBLIC_URL + '/images/blog/blog-1.jpg';

const insightItems = [
  {
    title: 'SAP Transformation Strategy',
    short: 'Build phased SAP transformation roadmaps that balance continuity and modernization.',
    date: '2026-03-28',
    image: sampleImage,
    category: 'SAP',
    type: 'Executive perspectives',
    slug: 'sap-transformation-strategy',
    description:
      'Full article: Build phased SAP transformation roadmaps that balance operational continuity with long-term modernization. Includes approaches to minimize risk, stage migrations, and measure outcomes.',
    faq: [
      { q: 'What is the first step?', a: 'Assess current landscape and define target operating model.' },
      { q: 'What is the first step?', a: 'Assess current landscape and define target operating model.' },
      { q: 'What is the first step?', a: 'Assess current landscape and define target operating model.' },
      { q: 'What is the first step?', a: 'Assess current landscape and define target operating model.' },
    ],
    sections: [
      {
        title: 'Overview',
        content: 'SAP transformation is a critical investment for enterprises seeking to modernize their ERP infrastructure. This comprehensive guide outlines the key phases and considerations for building a phased transformation roadmap that maintains operational continuity while achieving long-term modernization goals. We cover strategic planning, risk mitigation, and success measurement strategies.'
      },
      {
        title: 'Key Challenges',
        content: 'Organizations face several critical challenges during SAP transformation including managing data migration complexity, minimizing downtime, ensuring user adoption, integrating legacy systems, and maintaining business continuity. Understanding these challenges upfront allows for better planning and risk mitigation strategies.'
      },
      {
        title: 'Phased Approach',
        content: 'A phased transformation approach breaks the migration into manageable stages: Assessment Phase (3-4 months), Planning Phase (2-3 months), Build Phase (6-8 months), Testing Phase (2-3 months), and Go-Live Phase (1-2 months). Each phase has specific deliverables, milestones, and success criteria.'
      },
      {
        title: 'Risk Mitigation',
        content: 'Effective risk mitigation strategies include maintaining parallel systems during transition, comprehensive data validation, extensive testing protocols, change management programs, and contingency planning. Identify risks early and develop mitigation strategies for each identified risk.'
      },
      {
        title: 'Measuring Success',
        content: 'Success metrics should be defined early and tracked throughout the transformation. Key indicators include system uptime, data accuracy rates, user adoption rates, cost savings achieved, and time-to-value. Regular reviews and adjustments ensure alignment with business objectives.'
      },
    ],
  },
  {
    title: 'Finance Modernization',
    short: 'Modernizing finance operations, reporting, and visibility across teams.',
    date: '2026-02-15',
    image: sampleImage,
    category: 'SAP',
    type: 'Case studies',
    slug: 'finance-modernization',
    description:
      'Full article: Practical approaches for modernizing finance operations, reporting, and performance visibility across enterprise teams.',
    faq: [{ q: 'How long does this take?', a: 'Typically 6-18 months depending on scope.' }],
    sections: [
      {
        title: 'Current State Assessment',
        content: 'Modern finance operations require a thorough assessment of current processes, systems, and team capabilities. This includes evaluating existing finance systems, identifying bottlenecks, measuring key performance indicators, and understanding stakeholder needs.'
      },
      {
        title: 'Automation Opportunities',
        content: 'Finance modernization involves identifying and implementing automation opportunities across accounts payable, accounts receivable, general accounting, and financial reporting. RPA and intelligent automation can reduce manual work by 50-70%.'
      },
      {
        title: 'Cloud-Based Solutions',
        content: 'Cloud-based finance platforms offer scalability, accessibility, and real-time insights. Solutions should integrate with existing ERP systems and provide comprehensive reporting capabilities for better decision-making.'
      },
      {
        title: 'Data Integration',
        content: 'Successful modernization requires seamless integration of financial data from multiple sources. Implementing data lakes and business intelligence tools enables better visibility and faster insights.'
      },
      {
        title: 'Change Management',
        content: 'Finance teams must adapt to new processes and tools. Comprehensive training programs, change management strategies, and ongoing support ensure successful adoption and maximize value realization.'
      },
    ],
  },
  {
    title: 'Data Platform Architecture',
    short: 'Reference architectures for scalable, cloud-ready data platforms.',
    date: '2026-01-10',
    image: sampleImage,
    category: 'Data Platform',
    type: 'Whitepapers',
    slug: 'data-platform-architecture',
    description:
      'Full article: Reference architectures for scalable, cloud-ready data platforms that support analytics, AI, and governance needs.',
    faq: [{ q: 'Which cloud providers?', a: 'Designs are cloud-agnostic with examples for AWS, Azure, and GCP.' }],
    sections: [
      {
        title: 'Architecture Foundation',
        content: 'Modern data platforms require a solid architectural foundation that separates storage, processing, and consumption layers. This enables scalability, flexibility, and cost optimization while maintaining data governance and security standards.'
      },
      {
        title: 'Data Ingestion',
        content: 'Efficient data ingestion from multiple sources (batch and real-time) is critical. Implement scalable ingestion pipelines using managed services, establish data quality checks, and monitor for anomalies to ensure reliable data availability.'
      },
      {
        title: 'Storage & Processing',
        content: 'Separate storage from compute to optimize costs and performance. Use cloud data warehouses for structured data, data lakes for raw data, and specialized storage for semi-structured and unstructured data.'
      },
      {
        title: 'Governance & Security',
        content: 'Implement comprehensive data governance frameworks including data cataloging, quality management, access controls, and compliance monitoring. Security should be built into every layer of the platform.'
      },
      {
        title: 'Analytics & Insights',
        content: 'The platform should support diverse analytics workloads: business intelligence, advanced analytics, machine learning, and real-time dashboards. Enable self-service analytics while maintaining governance.'
      },
    ],
  },
  {
    title: 'Responsible AI In The Enterprise',
    short: 'Frameworks for introducing AI responsibly with oversight and explainability.',
    date: '2025-11-05',
    image: sampleImage,
    category: 'AI',
    type: 'Blog posts',
    slug: 'responsible-ai-enterprise',
    description:
      'Full article: Frameworks for introducing AI responsibly with human oversight, quality controls, explainability, and policy alignment.',
    faq: [{ q: 'What is explainability?', a: 'Techniques that make model reasoning interpretable to humans.' },

    { q: 'What is explainability?', a: 'Techniques that make model reasoning interpretable to humans.' },
    { q: 'What is explainability?', a: 'Techniques that make model reasoning interpretable to humans.' },
    { q: 'What is explainability?', a: 'Techniques that make model reasoning interpretable to humans.' }
    ],
    sections: [
      {
        title: 'AI Governance Framework',
        content: 'Establish clear governance structures for AI initiatives. Define roles and responsibilities, approval processes, and escalation procedures. Create an AI steering committee with representation from business, IT, legal, and ethics teams.'
      },
      {
        title: 'Model Explainability',
        content: 'Implement explainability techniques (SHAP, LIME) to make AI decision-making transparent. Business stakeholders should understand why the model made a specific recommendation or decision, building trust and enabling oversight.'
      },
      {
        title: 'Quality & Testing',
        content: 'Establish rigorous testing protocols for AI models including performance testing on diverse datasets, bias detection, adversarial testing, and continuous monitoring in production for model drift.'
      },
      {
        title: 'Ethical Guidelines',
        content: 'Define ethical principles for AI use within your organization. Address fairness, transparency, accountability, and privacy concerns. Regularly audit AI systems to identify and mitigate bias and unintended consequences.'
      },
      {
        title: 'Risk Management',
        content: 'Develop comprehensive risk management frameworks for AI. Identify potential risks, implement mitigation strategies, maintain human oversight for critical decisions, and establish rollback procedures.'
      },
    ],
  },
  {
    title: 'Governance And Compliance',
    short: 'Governance models and controls for transparency and risk reduction.',
    date: '2025-09-20',
    image: sampleImage,
    category: 'Governance',
    type: 'Executive perspectives',
    slug: 'governance-and-compliance',
    description:
      'Full article: Governance models and controls that improve transparency, reduce risk, and support sustainable digital transformation.',
    faq: [{ q: 'Who owns governance?', a: 'Stakeholders across IT, Legal, and Business should share ownership.' }],
    sections: [
      {
        title: 'Governance Structure',
        content: 'Effective governance requires clear organizational structure with defined roles, responsibilities, and decision-making authority. Establish governance committees at different levels (strategic, tactical, operational) with appropriate executive sponsorship.'
      },
      {
        title: 'Policy & Procedures',
        content: 'Develop comprehensive policies covering data governance, information security, IT operations, and compliance. Document procedures for common scenarios, exceptions, and escalations. Ensure policies align with regulatory requirements.'
      },
      {
        title: 'Risk Management',
        content: 'Implement enterprise risk management frameworks. Identify risks, assess impact and probability, develop mitigation strategies, and continuously monitor risk indicators. Regular risk assessments should inform strategic decisions.'
      },
      {
        title: 'Compliance & Auditing',
        content: 'Maintain compliance with applicable regulations (GDPR, SOX, HIPAA, etc.). Implement control frameworks, perform regular audits, document compliance evidence, and remediate identified gaps.'
      },
      {
        title: 'Performance Metrics',
        content: 'Measure governance effectiveness through KPIs such as policy compliance rates, risk remediation timelines, audit findings, incident response times, and stakeholder satisfaction. Use metrics to continuously improve governance.'
      },
    ],
  },
  {
    title: 'Emerging Digital Ecosystems',
    short: 'How organizations evaluate Web3 and decentralized patterns.',
    date: '2025-07-12',
    image: sampleImage,
    category: 'Web3 / Blockchain',
    type: 'Blog posts',
    slug: 'emerging-digital-ecosystems',
    description:
      'Full article: How organizations evaluate Web3 and decentralized patterns to unlock trust, interoperability, and future-ready models.',
    faq: [{ q: 'Is Web3 mature?', a: 'It is emerging; pilots are recommended before broad adoption.' }],
    sections: [
      {
        title: 'Blockchain Fundamentals',
        content: 'Understand blockchain technology basics: distributed ledgers, consensus mechanisms, smart contracts, and immutability. Different blockchain platforms (Bitcoin, Ethereum, Solana) have different characteristics, performance profiles, and use cases.'
      },
      {
        title: 'Use Case Evaluation',
        content: 'Not all business problems require blockchain. Evaluate whether you need decentralization, transparency across untrusted parties, immutable audit trails, or direct peer-to-peer transactions. Blockchain adds complexity; traditional databases are often sufficient.'
      },
      {
        title: 'Technology Selection',
        content: 'Choose blockchain platforms based on throughput requirements, latency, scalability, energy consumption, and ecosystem maturity. Consider public blockchains (Ethereum), private/permissioned (Hyperledger), or consortium approaches.'
      },
      {
        title: 'Pilot & Testing',
        content: 'Start with controlled pilots to test assumptions, identify challenges, and evaluate business value. Focus on MVP scope, measurable metrics, and clear success criteria. Document learnings for broader rollout decisions.'
      },
      {
        title: 'Organizational Readiness',
        content: 'Successful Web3 adoption requires organizational change, new skill development, updated governance, and regulatory alignment. Build cross-functional teams with blockchain expertise, legal, and business stakeholders.'
      },
    ],
  },
];

const contentTypes = ['Blog posts', 'Case studies', 'Whitepapers', 'Executive perspectives'];

// Export insights data for use in other components
export { insightItems };
export default function Insights() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');

  const handleNavigateHome = () => {
    navigate(ROUTES.HOME);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleViewAllInsights = () => {
    setActiveTab('All');
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleSubscribe = () => {
    alert('Subscription feature coming soon!');
  };

  const handleOpenInsight = (item) => {
    if (item) {
      navigate(getInsightDetailPath(item.slug));

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const sliderRef = useRef(null);
  const [insightsData, setInsightsData] = useState({
    insights: [],
    blog_categories: []
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/insights`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "api-username": `${process.env.REACT_APP_API_URL_USERNAME}`,
        "api-password": `${process.env.REACT_APP_API_URL_PASSWORD}`
      },
      body: JSON.stringify({})
    })
      .then((response) => response.json())
      .then((data) => {
        setInsightsData(data);
        if (data.blog_categories?.length > 0) {
          setActiveTab(data.blog_categories[0].name);
        }
      })
      .catch((error) => {
        console.log(error);
      }).finally(() => {
        setLoading(false);
      });
  }, []);
  const visibleItems = useMemo(() => {
    if (activeTab === 'All') return insightsData.insights;
    return insightsData.insights.filter(
      (item) => item.category.name === activeTab
    );
  }, [activeTab]);
  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );
  }
  return (
    <div className="insights-page">
      <section className="insights-banner inner-page-banner" style={{ backgroundImage: `url(${insightsBanner})` }}>
        <div className="insights-banner-overlay inner-page-banner-overlay" />
        <div className="container insights-banner-inner inner-page-banner-inner">
          <h1 className="inner-page-banner-title">Insights</h1>
        </div>
      </section>

      <div className="inner-page-breadcrumb-wrap">
        <div className="container">
          <nav className="inner-page-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="inner-page-home" onClick={handleNavigateHome}>
              Home
            </button>
            <span className="inner-page-sep"><i className="bi bi-chevron-right" /></span>
            <span className="inner-page-current">Insights</span>
          </nav>
        </div>
      </div>

      <section className="insights-main-section">
        <div className="container">
          <div className="insights-intro-card" data-aos="fade-up">
            <h2>Insights and thought leadership</h2>
            <p>
              Explore practical guidance, executive perspectives, and applied examples across SAP transformation,
              data platform strategy, AI, governance, and digital innovation.
            </p>
          </div>

          <div className="insights-tabs-wrap" data-aos="fade-up">

            {insightsData.blog_categories.map((tab, index) => (
              <button
                key={tab.id}
                className={`insights-tab-btn${activeTab === tab.name || (index === 0 && !activeTab) ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.name)}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <div className="insights-grid" data-aos="fade-up" data-aos-delay="80">
            {visibleItems.length > 0 ? (
              visibleItems.map((item) => (
                <article
                  role="button"
                  tabIndex={0}
                  className="insight-card"
                  key={`${item.name}-${item.id}`}
                  onClick={() => handleOpenInsight(item)}
                  onKeyDown={(e) =>
                    e.key === "Enter" ? handleOpenInsight(item) : null
                  }
                >
                  <div
                    className="insight-thumb"
                    style={{
                      backgroundImage: `url(${process.env.REACT_APP_IMAGE_BASE_URL}/${item.folder_path}/${item.image})`,
                    }}
                  />

                  <div className="insight-card-top">
                    <span className="insight-category">
                      {item.category?.name}
                    </span>

                    <span className="insight-type">
                      {item.type}
                    </span>
                  </div>

                  <h3>{item.name}</h3>

                  <p className="insight-short">
                    {item.short_description}
                  </p>

                  <button
                    className="insight-readmore"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInsight(item);
                    }}
                  >
                    Explore More
                  </button>

                  <div className="insight-card-date">
                    Published on{" "}
                    {item.post_date
                      ? new Date(item.post_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                      : ""}
                  </div>
                </article>
              ))
            ) : (
              <p>No insights found.</p>
            )}
          </div>

          {/* <div className="insights-types-section" data-aos="fade-up" data-aos-delay="120">
            <h3>Content types</h3>
            <div className="insights-type-pills">
              {contentTypes.map((type) => (
                <span className="insights-type-pill" key={type}>{type}</span>
              ))}
            </div>
          </div> */}

          <div className="insights-cta" data-aos="fade-up" data-aos-delay="160">
            <h3>Want more practical insights?</h3>
            <div className="insights-cta-actions">
              <button className="insights-btn insights-btn-primary" onClick={handleViewAllInsights}>
                View all insights
              </button>
              <button className="insights-btn insights-btn-secondary" onClick={handleSubscribe}>
                Subscribe for updates
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
